import React from "react";
import cn from "classnames";
import Check from "storefront/components/Icons/Information/Check";
import Callout from "storefront/components/Callout";

import styles from "./Checkbox.module.scss";

type Props = {
  children: React.ReactNode;
  className?: string;
  /** Also used as the `for` attribute on the label */
  id: string;
  name?: string;
  value: string | number;
  onChange?: (e: React.SyntheticEvent<HTMLInputElement>) => unknown;

  /** Read this from the [React Docs](https://reactjs.org/docs/dom-elements.html#checked)
   * to understand how this props is used in React
   */
  checked?: boolean;
  disabled?: boolean;
} & React.HTMLAttributes<HTMLInputElement>;

/**
 * Checkbox input, includes the label which you can pass children to.
 *
 * > Please read the [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox)
 * docs to understand the different props and how to properly use checkboxes.
 *
 * > The component spreads additional props
 */
const Checkbox = ({
  children,
  className,
  id,
  name,
  value,
  onChange,
  checked,
  ...props
}: Props) => (
  <label className={cn(styles.checkbox, className)} htmlFor={id}>
    <input
      {...props}
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      checked={checked}
      className={styles.input}
      type="checkbox"
    />
    <span className={styles.check} aria-hidden>
      <Check className={styles.checkIcon} />
    </span>
    <Callout as="span" className={styles.label}>
      {children}
    </Callout>
  </label>
);

export default Checkbox;
